import { supabase } from '@/lib/supabase';

export type AgendaEvent = {
  id: string;
  title: string;
  subject: string;
  starts_at: string | null;
  ends_at: string | null;
  instructions: string;
};

/** Encore à venir ou en cours (non terminé). */
export function isUpcomingAgendaEvent(
  event: Pick<AgendaEvent, 'starts_at' | 'ends_at'>,
  ref = Date.now(),
): boolean {
  if (event.ends_at) return new Date(event.ends_at).getTime() >= ref;
  if (!event.starts_at) return true;
  return new Date(event.starts_at).getTime() >= ref;
}

export function formatAgendaDate(iso: string | null | undefined, locale: 'fr' | 'en' = 'fr'): string {
  if (!iso) return locale === 'en' ? 'Date to be confirmed' : 'Date à confirmer';
  return new Date(iso).toLocaleString(locale === 'en' ? 'en-GB' : 'fr-FR', {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export function formatAgendaRange(
  startsAt: string | null | undefined,
  endsAt: string | null | undefined,
  locale: 'fr' | 'en' = 'fr',
): string {
  if (!startsAt && !endsAt) return locale === 'en' ? 'Date to be confirmed' : 'Date à confirmer';
  if (!endsAt) return formatAgendaDate(startsAt, locale);
  if (!startsAt) {
    const prefix = locale === 'en' ? 'Until' : 'Jusqu’au';
    return `${prefix} ${formatAgendaDate(endsAt, locale)}`;
  }

  const start = new Date(startsAt);
  const end = new Date(endsAt);
  const sameDay =
    start.getFullYear() === end.getFullYear() &&
    start.getMonth() === end.getMonth() &&
    start.getDate() === end.getDate();

  const dateOpts: Intl.DateTimeFormatOptions = {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  };
  const timeOpts: Intl.DateTimeFormatOptions = { hour: '2-digit', minute: '2-digit' };
  const loc = locale === 'en' ? 'en-GB' : 'fr-FR';

  if (sameDay) {
    return `${start.toLocaleDateString(loc, dateOpts)} · ${start.toLocaleTimeString(loc, timeOpts)} – ${end.toLocaleTimeString(loc, timeOpts)}`;
  }

  return `${formatAgendaDate(startsAt, locale)} → ${formatAgendaDate(endsAt, locale)}`;
}

export async function fetchUpcomingAgendaEvents(limit = 8): Promise<AgendaEvent[]> {
  const { data, error } = await supabase
    .from('agenda_events')
    .select('id, title, subject, starts_at, ends_at, instructions')
    .order('starts_at', { ascending: true, nullsFirst: false });

  if (error) throw error;

  return ((data ?? []) as AgendaEvent[])
    .filter(isUpcomingAgendaEvent)
    .slice(0, limit);
}
